import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import {
  Box,
  List,
  Typography,
  ListItem,
  ListItemText,
  Button,
  Stack,
  ListItemIcon,
  styled,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { IconButton } from "@mui/material";
import { ReactComponent as BulletIcon } from "../../assets/images/bullet_icon.svg";
import { ReactComponent as CloseOutlined } from "../../assets/icons/close_icon.svg";
import background from "../../assets/images/recent-winner-bg.svg";
import { useEffect } from "react";
import { doSubscribe } from "../../reducers/subscription/subscription.service";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import cancelImage from "../../assets/images/cancel_subscription.png";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import dayjs from "dayjs";
import { doGetUser } from "../../reducers/user/user.service";
import { doCancelSubscription } from "../../reducers/subscription/subscription.service";
import { doLogout } from "../../reducers/auth/auth.service";
import { useNavigate } from "react-router-dom";
import removeAccessToken from "../../reducers/auth/auth.slice";
import { doUpdateUser } from "../../reducers/user/user.service";

async function fetchSubscriptionData(accessToken) {
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/subscription`,
      config
    );
    const data = response.data?.data;
    return data;
  } catch (error) {
    return [];
  }
}

const Image = styled("img")``;
const CancelConfirmation = ({ open, handleDialogClose }) => {
  const dispatch = useDispatch();
  return (
    <Dialog
      scroll="body"
      open={open}
      onClose={handleDialogClose}
      disableScrollLock={false}
      slotProps={{
        paper: {
          component: "form",
          sx: {
            p: { xs: "15px !important", sm: "30px !important" },
            borderRadius: { xs: "20px !important", sm: "50px !important" },
            width: "500px !important",
          },
        },
      }}
    >
      <Image src={cancelImage} sx={{ width: "200px", mx: "auto" }} />

      <DialogTitle
        variant="h3"
        sx={{
          textAlign: "center",
          p: 0,
          mt: "15px",
          fontWeight: 700,
        }}
      >
        Are you sure you want to cancel?
      </DialogTitle>

      <Typography
        sx={{
          color: "white",
          textAlign: "center",
          fontFamily: "secondaryFontFamily",
          fontSize: "14px",
          fontWeight: 300,
          mb: "30px",
          mx: "auto",
          width: "350px",
        }}
      >
        You’ll lose access to premium features after your current billing period
        ends.
      </Typography>

      <IconButton
        aria-label="close"
        onClick={handleDialogClose}
        sx={{
          position: "absolute",
          right: { sm: 20, xs: 10 },
          top: { sm: 20, xs: 10 },
          fontSize: "25px",
          color: "#999999",
        }}
      >
        <CloseOutlined />
      </IconButton>
      <Stack
        direction={"row"}
        gap={3}
        sx={{ justifyContent: "space-between", mt: "50px" }}
      >
        <Button
          autoFocus
          variant="contained"
          onClick={() => handleDialogClose()}
          sx={{
            mb: "15px",
            width: "50%",
            p: "10px 20px 7px 20px !important",
            bgcolor: "#D2D2D2",
            color: "#111827",
          }}
        >
          Keep Subscription
        </Button>
        <Button
          variant="contained"
          onClick={async () => {
            const responseValue = await dispatch(doCancelSubscription({}));
            if (doCancelSubscription.fulfilled.match(responseValue)) {
              await dispatch(doGetUser());
              enqueueSnackbar("Subscription successfully canceled", {
                variant: "success",
              });
              handleDialogClose();
            } else {
              enqueueSnackbar("Error canceling subscription", {
                variant: "error",
              });
            }
          }}
          sx={{
            mb: "15px",
            width: "50%",
            p: "10px 20px 7px 20px !important",
            bgcolor: "red",
          }}
        >
          Cancel Subscription
        </Button>
      </Stack>
    </Dialog>
  );
};

const SubscriptionDialog = ({ open, handleDialogClose }) => {
  const navigate = useNavigate();
  const [allPlans, setAllPlans] = useState([]);
  const dispatch = useDispatch();
  const [openConfirm, setOpenConfirm] = useState(false);
  const handleCloseConfirm = () => setOpenConfirm(false);
  const loggedIn = useSelector((state) => state.auth.accessToken);
  const isTelegramJoined = useSelector((state) => state.user.isTelegramJoined);
  const [disableButton, setDisableButton] = useState(false);
  const planCancel = useSelector((state) => state.user.planCancel);
  const currExpiry = useSelector((state) => state.user.planExpiry);
  const authorized = currExpiry > new Date();
  const handleDisableButton = () => setDisableButton(true);

  useEffect(() => {
    if (planCancel !== null && authorized) handleDisableButton();
  }, [planCancel, authorized]);

  useEffect(() => {
    const fetchFeatures = async () => {
      const data = await fetchSubscriptionData(loggedIn);
      setAllPlans(data);
    };
    fetchFeatures();
  }, [authorized]);

  useEffect(() => {
    const fetchFeatures = async () => {
      const data = await fetchSubscriptionData(loggedIn);
      setAllPlans(data);
    };
    fetchFeatures();
  }, [loggedIn]);

  useEffect(() => {
    const fetchFeatures = async () => {
      const data = await fetchSubscriptionData(loggedIn);
      setAllPlans(data);
    };
    fetchFeatures();
  }, []);

  const customerId = useSelector((state) => state.user.customerId);
  const handleSubscription = async (priceId) => {
    const responseValue = await dispatch(
      doSubscribe({ priceId: priceId, customerId: customerId })
    );
    if (
      doSubscribe.fulfilled.match(responseValue) ||
      doSubscribe.rejected.match(responseValue)
    ) {
      setDisableButton(false);
    }
    if (responseValue.payload.status === 401) {
      await dispatch(doLogout());
      await dispatch(removeAccessToken());
      navigate("/");
    }
  };

  const currentPlanId = useSelector((state) => state.user.planUid);

  return (
    <>
      <Dialog
        scroll="body"
        open={open}
        onClose={handleDialogClose}
        disableScrollLock={false}
        fullWidth
        slotProps={{
          paper: {
            component: "form",
            sx: {
              maxWidth: "700px",
              borderRadius: { xs: "20px !important", sm: "50px !important" },
              pb: { sm: "60px !important", xs: "20px !important" },
              px: { xs: "20px !important", sm: "56px !important" },
            },
          },
        }}
      >
        <DialogTitle
          variant="h3"
          sx={{
            textAlign: "center",
            p: 0,
            mt: "10px",
            fontWeight: 700,
          }}
        >
          SUBSCRIPTION
        </DialogTitle>

        <Typography
          sx={{
            color: "white",
            textAlign: "center",
            fontFamily: "secondaryFontFamily",
            fontSize: "14px",
            fontWeight: 300,
            mb: "20px",
          }}
        >
          Update your profile, security and preferences in one place
        </Typography>

        <IconButton
          aria-label="close"
          onClick={handleDialogClose}
          sx={{
            position: "absolute",
            right: 20,
            top: 20,
            fontSize: "25px",
            color: "#999999",
          }}
        >
          <CloseOutlined />
        </IconButton>
        <Box
          sx={{
            textAlign: "center",
            borderRadius: "20px",
            backgroundColor: "#192235",
            mt: "40px",
          }}
        >
          {allPlans.map((plan, index) => (
            <Box key={index}>
              <Box
                key={index}
                sx={{
                  display: "flex",
                  background: `url(${background})`,
                  backgroundColor: "primary.main",
                  justifyContent: "space-between",
                  borderTopLeftRadius: "20px",
                  borderTopRightRadius: "20px",
                  p: "20px",
                }}
              >
                <Box sx={{ textAlign: "left" }}>
                  <Typography
                    variant="h3"
                    sx={{ color: "white", fontWeight: 700 }}
                  >
                    {plan.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: "white",
                      fontSize: "13px",
                      fontWeight: 500,
                      fontFamily: "secondaryFontFamily",
                      maxWidth: "320px",
                    }}
                  >
                    {plan.description}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "left" }}>
                  <Typography
                    sx={{
                      color: "white",
                      fontWeight: 700,
                      fontSize: "40px",
                      lineHeight: "48px",
                      textAlign: "right",
                    }}
                  >
                    {plan.price}
                  </Typography>
                  <Typography
                    sx={{
                      color: "white",
                      fontSize: "16px",
                      fontWeight: 500,
                      fontFamily: "secondaryFontFamily",
                    }}
                  >
                    /per month
                  </Typography>
                </Box>
              </Box>
              <List sx={{ mx: "20px", my: "10px" }}>
                {plan.planData.length > 0 ? (
                  plan.planData.map((feature, index) => (
                    <ListItem
                      disableGutters
                      disablePadding
                      key={index}
                      sx={{
                        alignItems: "flex-start",
                        ".MuiListItemText-primary": {
                          color: "white !important",
                          fontSize: "16px !important",
                          fontWeight: 500,
                        },
                        ".MuiListItemText-secondary": {
                          color: "rgba(255, 255, 255, 0.8) !important",
                          fontSize: "14px !important",
                          fontWeight: 200,
                          mt: "8px",
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{ minWidth: "45px !important", mt: "5px" }}
                      >
                        <BulletIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={feature["title"]}
                        secondary={feature["description"]}
                      ></ListItemText>
                    </ListItem>
                  ))
                ) : (
                  <div style={{ color: "white" }}>Please wait ... </div>
                )}
              </List>{" "}
              <Button
                variant="contained"
                disabled={disableButton}
                onClick={() => {
                  if (plan?.planUid === currentPlanId && authorized) {
                    setOpenConfirm(true);
                  } else {
                    handleSubscription(plan.priceId);
                    handleDisableButton();
                  }
                }}
                sx={{
                  pt: "12px",
                  pb: "8px !important",
                  fontSize: "18px",
                  fontWeight: 400,
                  width: "88%",
                  mt: "10px",
                  mb: "30px",
                  backgroundColor:
                    planCancel !== null
                      ? "disable"
                      : plan?.planUid === currentPlanId && authorized
                      ? "red !important"
                      : "none",
                }}
              >
                {planCancel !== null && authorized
                  ? `You can subscribe again after ${dayjs
                      .unix(currExpiry / 1000)
                      .format("DD-MM-YYYY")}`
                  : plan?.planUid === currentPlanId && authorized
                  ? "Cancel Subscription"
                  : "subscribe now"}
              </Button>
            </Box>
          ))}
        </Box>
        <FormControlLabel
          onChange={async () => {
            if (isTelegramJoined) {
              // unjoin telegram
              const responseValue = await dispatch(
                doUpdateUser({ isTelegramJoined: false })
              );
              if (doUpdateUser.fulfilled.match(responseValue)) {
                enqueueSnackbar("Successfuly removed from telegram", {
                  variant: "success",
                });
              } else {
                enqueueSnackbar("Error removing from telegram", {
                  variant: "error",
                });
              }
            } else {
              // join telegram
              const responseValue = await dispatch(
                doUpdateUser({ isTelegramJoined: true })
              );
              if (doUpdateUser.fulfilled.match(responseValue)) {
                enqueueSnackbar("Successfuly joined telegram", {
                  variant: "success",
                });
              } else {
                enqueueSnackbar("Error joining telegram", {
                  variant: "error",
                });
              }
            }
          }}
          sx={{
            ".MuiCheckbox-root": {
              padding: "0px !important",
            },
            ".MuiTypography-root": {
              color: "white",
              fontFamily: "secondaryFontFamily",
            },
            gap: 1,
            ml: "0px",
            mt: "20px",
          }}
          control={<Checkbox checked={isTelegramJoined} />}
          label="Join telegram"
        />
      </Dialog>
      <CancelConfirmation
        open={openConfirm}
        handleDialogClose={handleCloseConfirm}
      />
    </>
  );
};

export default SubscriptionDialog;
